/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import { updateConfigValue, getConfigValue } from "../../../../sharedServices/configService";
import MessageDialog from "./MessageDialog";
import { useHistory } from "react-router-dom";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../../utility/errorCodes";
import { ConfirmDialog } from "../../../../components/ConfirmDialog/ConfirmDialog";
import { ActionButtons } from "../../../../components/Common";

const COMPONENT_NAME = "Admin settingsManager/components/QueueManagement/ForceCloseButton";

/**
 * This component displays the button for initiating or reopening a forced closure on the contact center.
 */
export const ForceCloseButton = ({ setIsLoading }) => {
	const [closed, setClosed] = React.useState(undefined);
	const [confirmOpen, setConfirmedOpen] = React.useState(false);
	const [closedMessageOpen, setClosedMessageOpen] = React.useState(false);
	const [openMessageOpen, setOpenMessageOpen] = React.useState(false);
	const history = useHistory();

	React.useEffect(() => {
		if (closed === undefined) {
			const getConfig = async () => {
				const configValue = await getConfigValue("IsForceClosed");
				setClosedMessageOpen((configValue && configValue.IsForceClosed) || false);
				setClosed((configValue && configValue.IsForceClosed) || false);
			};

			getConfig();
		}
	}, [closed]);

	return (
		closed !== undefined &&
		((!closed && (
			<>
				<ActionButtons showSave onSave={() => setConfirmedOpen(true)} saveLabel="Force Close" />
				<MessageDialog
					open={openMessageOpen}
					title={"Contact Center Now Open"}
					message={"The contact center is now open."}
					onClose={() => {
						setOpenMessageOpen(false);
						history.push("/settings/queues");
					}}
				/>
				<ConfirmDialog
					title={"Force Close Contact Center?"}
					message={
						"Are you sure you want to proceed with closing the contact center? This will cause all new incoming contacts to be disconnected."
					}
					confirmOpen={confirmOpen}
					onClose={async (close) => {
						try {
							if (close) {
								setIsLoading(true);
								await updateConfigValue("IsForceClosed", true);
								setClosed(true);
								history.push("/settings/queues");
							}
							setConfirmedOpen(false);
						} catch (error) {
							v2.error(`Error while attempting to force close`, {
								additionalData: { error: errorToObject(error) },
								componentName: COMPONENT_NAME,
								logCode: errorCodes.FORCE_CLOSE_ERROR,
							});
						} finally {
							setIsLoading(false);
						}
					}}
				/>
			</>
		)) || (
			<>
				<ActionButtons
					showSave
					saveLabel="Resume Normal Hours"
					onSave={async () => {
						try {
							setIsLoading(true);
							await updateConfigValue("IsForceClosed", false);
							setOpenMessageOpen(true);
							setClosed(false);
							setIsLoading(false);
						} catch (error) {
							v2.error(`Error while attempting to undo force close`, {
								additionalData: { error: errorToObject(error) },
								componentName: COMPONENT_NAME,
								logCode: errorCodes.UNDO_FORCE_CLOSE_ERROR,
							});
						}
					}}
				/>
				<MessageDialog
					open={closedMessageOpen}
					title={"Contact Center Forced Closed"}
					message={
						"The contact center is currently closed. To reopen the contact center please click resume normal hours."
					}
					onClose={() => setClosedMessageOpen(false)}
				/>
			</>
		))
	);
};
